import React from "react";
import apiService from "../../../services/ApiService";
import TailwindSelect from "../../Tailwind/TailwindSelect";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../../shared/hooks/queries/useSectionLoader";
import snakeToPascal from "../../../tools";


export default function SensitivityEnumField({ name, item, field, currentSensitivity }) {
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const queryClient = useQueryClient();

    const invalidateQueries = () => {
        // Only invalidate section-data cache — do NOT invalidate SCENARIO_TREE_KEY (see useUpdateFieldValue.ts)
        queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
    };

    const sensitivityField = item.sensitivityModelData.fields[field.name];

    const originalValue = (field.currentValue.value) ? field.currentValue.value : "";
    const initialValue = (sensitivityField) ? sensitivityField.value.value : originalValue

    const [fieldValue, setFieldValue] = React.useState(initialValue as string);
    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();

    const handleChange = (value) => {
        triggerSave();
        apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, value !== "" ? value : null).then(
            async(response) => {
                if (response) {
                    const updatedField = await response.json()
                    item.sensitivityModelData.fields[field.name] = updatedField
                    resolveSave();
                    invalidateQueries();
                }
            }
        )
        setFieldValue(value);
    }

    // Display-only: keep the canonical enum key as the value, prettify the label.
    const selectOptions = field.choices.map(choice => ({
        value: choice,
        label: snakeToPascal(choice)
    }));

    const resetSensitivityValue = () => {
        apiService.deleteSensitivityValue(sensitivityField.id).then(
            async(response) => {
                if (response) {
                    delete item.sensitivityModelData.fields[field.name];
                    setFieldValue(originalValue)
                    invalidateQueries();
                }
            }
        )
    }

    return (
        <div className="flex flex-row items-center">
            {!isReadOnly && (
                <button
                    onClick={resetSensitivityValue}
                    disabled={!sensitivityField}
                    className="mr-2 h-10 w-10 flex items-center justify-center text-slate-600 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <span className="material-symbols-outlined text-[30px]">restart_alt</span>
                </button>
            )}
            <TailwindSelect
                value={fieldValue}
                onChange={handleChange}
                options={selectOptions}
                placeholder={name}
                disabled={isReadOnly}
                className="min-w-[200px]"
                autoWidth
            />
            <SaveIndicator status={saveStatus} className="ml-2" />
        </div>
    )
}
